import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import Maintenance from './pages/Maintenance';
import './App.css';

// Lazy-loaded routes — incarcate doar cand e nevoie (reduce bundle initial ~35%)
const Rooms       = lazy(() => import('./pages/Rooms'));
const RoomDetail  = lazy(() => import('./pages/RoomDetail'));
const Menu        = lazy(() => import('./pages/Menu'));
const Services    = lazy(() => import('./pages/Services'));
const About       = lazy(() => import('./pages/About'));
const Contact     = lazy(() => import('./pages/Contact'));
const Admin       = lazy(() => import('./pages/Admin'));
const CookiePolicy     = lazy(() => import('./pages/CookiePolicy'));
const PrivacyPolicy    = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions  = lazy(() => import('./pages/TermsConditions'));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Checks maintenance mode and redirects public pages when active
function MaintenanceGuard({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState(null);

  useEffect(() => {
    fetch('/api/maintenance')
      .then(r => r.json())
      .then(data => setMaintenance(data))
      .catch(() => setMaintenance({ enabled: false, message: '' }));
  }, [pathname]);

  useEffect(() => {
    if (!maintenance) return;
    const isProtected = !pathname.startsWith('/admin') && pathname !== '/login' && pathname !== '/mentenanta';
    if (maintenance.enabled && isProtected) {
      navigate('/mentenanta', { replace: true });
    }
    if (!maintenance.enabled && pathname === '/mentenanta') {
      navigate('/', { replace: true });
    }
  }, [maintenance, pathname, navigate]);

  if (maintenance === null) return null; // wait for check

  return children;
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <ScrollToTop />
        <MaintenanceGuard>
          <Suspense fallback={<div className="page-suspense-fallback" />}>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin/*" element={<Admin />} />
            <Route path="/mentenanta" element={<MaintenancePage />} />
            <Route
              path="/*"
              element={
                <>
                  <Navbar />
                  <main>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/camere" element={<Rooms />} />
                      <Route path="/camere/:slug" element={<RoomDetail />} />
                      <Route path="/meniu" element={<Menu />} />
                      <Route path="/servicii" element={<Services />} />
                      <Route path="/despre" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/politica-cookies" element={<CookiePolicy />} />
                      <Route path="/politica-confidentialitate" element={<PrivacyPolicy />} />
                      <Route path="/termeni-si-conditii" element={<TermsConditions />} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              }
            />
          </Routes>
          </Suspense>
        </MaintenanceGuard>
      </Router>
    </LanguageProvider>
  );
}

// Wrapper that fetches fresh message and site identity for the maintenance page
function MaintenancePage() {
  const [data, setData] = useState({ message: '', siteName: '', siteTagline: '' });
  useEffect(() => {
    Promise.all([
      fetch('/api/maintenance').then(r => r.json()).catch(() => ({})),
      fetch('/api/content?lang=ro').then(r => r.json()).catch(() => ({})),
    ]).then(([maint, content]) => {
      setData({
        message: maint.message || '',
        siteName: content.site_name || '',
        siteTagline: content.site_tagline || '',
      });
    });
  }, []);
  return <Maintenance message={data.message} siteName={data.siteName} siteTagline={data.siteTagline} />;
}

export default App;

